/**
 * Tenant Context
 *
 * Provides tenant branding information to the entire app.
 * Fetches tenant info based on subdomain before authentication.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Default branding when no tenant is found or for admin portal
const DEFAULT_BRANDING = {
  companyDisplayName: 'Quote This Job',
  logoUrl: '/logo.png',
  faviconUrl: '/favicon.ico',
  colors: {
    primary: '#2563eb',
    secondary: '#64748b',
    accent: '#10b981',
    headerBackground: '#1f2937',
    headerText: '#ffffff',
  },
};

// Admin portal branding
const ADMIN_BRANDING = {
  companyDisplayName: 'Admin Portal',
  logoUrl: null,
  faviconUrl: '/favicon.ico',
  colors: {
    primary: '#4f46e5', // Indigo
    secondary: '#64748b',
    accent: '#10b981',
    headerBackground: '#312e81',
    headerText: '#ffffff',
  },
};

const TenantContext = createContext(null);

/**
 * Extract subdomain from current hostname
 */
function getSubdomain() {
  const hostname = window.location.hostname;

  // Handle localhost development (e.g., acme.localhost:5173, acme.localhost)
  if (hostname.endsWith('.localhost') || hostname.includes('.localhost:')) {
    const parts = hostname.split('.localhost');
    return parts[0] || null;
  }

  // Ignore AWS Amplify URLs (e.g., main.d123abc.amplifyapp.com, branch-name.d123abc.amplifyapp.com)
  if (hostname.includes('.amplifyapp.com')) {
    return null;
  }

  // Handle production (e.g., acme.quotethisjob.com)
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    // Skip 'www' subdomain
    if (parts[0] === 'www') {
      return null;
    }
    return parts[0];
  }

  return null;
}

/**
 * Check if current subdomain is the admin portal
 */
function isAdminSubdomain() {
  const subdomain = getSubdomain();
  const adminSubdomain = process.env.REACT_APP_ADMIN_SUBDOMAIN || 'admin';
  return subdomain === adminSubdomain;
}

/**
 * Apply branding colors as CSS custom properties
 */
function applyBrandingColors(colors) {
  const root = document.documentElement;

  if (colors.primary) {
    root.style.setProperty('--color-primary', colors.primary);
  }
  if (colors.secondary) {
    root.style.setProperty('--color-secondary', colors.secondary);
  }
  if (colors.accent) {
    root.style.setProperty('--color-accent', colors.accent);
  }
  if (colors.headerBackground) {
    root.style.setProperty('--color-header-bg', colors.headerBackground);
  }
  if (colors.headerText) {
    root.style.setProperty('--color-header-text', colors.headerText);
  }
}

/**
 * Clear any tenant-injected icon/meta tags before re-applying.
 * Identified by data-tenant-icon="true" so we don't clobber index.html defaults
 * on the first render.
 */
function clearTenantIconTags() {
  document
    .querySelectorAll('link[data-tenant-icon="true"], meta[data-tenant-icon="true"]')
    .forEach((el) => el.remove());
}

function appendIconLink({ rel, href, sizes, type }) {
  if (!href) return;
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (sizes) link.sizes = sizes;
  if (type) link.type = type;
  link.dataset.tenantIcon = 'true';
  document.head.appendChild(link);
}

function appendMetaTag(name, content) {
  if (!content) return;
  const meta = document.createElement('meta');
  meta.name = name;
  meta.content = content;
  meta.dataset.tenantIcon = 'true';
  document.head.appendChild(meta);
}

/**
 * Apply all tenant favicons/touch icons to document head.
 *
 * Accepts the branding shape: { faviconUrl, faviconUrls: {size: url},
 * touchIconUrl, msTileImageUrl }. faviconUrl is treated as a fallback if
 * faviconUrls is empty.
 */
function updateFavicons({ faviconUrl, faviconUrls, touchIconUrl, msTileImageUrl }) {
  clearTenantIconTags();

  const sized = faviconUrls && Object.keys(faviconUrls).length > 0
    ? faviconUrls
    : faviconUrl
      ? { '32': faviconUrl }
      : {};

  Object.entries(sized).forEach(([size, href]) => {
    appendIconLink({ rel: 'icon', href, sizes: `${size}x${size}`, type: 'image/png' });
  });

  appendIconLink({ rel: 'apple-touch-icon', href: touchIconUrl });
  appendMetaTag('msapplication-TileImage', msTileImageUrl);
}

/**
 * Update document title
 */
function updateDocumentTitle(companyName) {
  document.title = companyName || 'Quote This Job';
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subdomain, setSubdomain] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Fetch tenant branding from API
  const fetchTenantBranding = useCallback(async (subdomainValue) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(
        `${apiUrl}/public/tenant?subdomain=${encodeURIComponent(subdomainValue)}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Subdomain "${subdomainValue}" not found`);
        }
        throw new Error('Failed to fetch tenant information');
      }

      const data = await response.json();
      return data.data; // API response wraps data
    } catch (err) {
      console.error('Failed to fetch tenant branding:', err);
      throw err;
    }
  }, []);

  // Initialize tenant context on mount
  useEffect(() => {
    async function initialize() {
      setIsLoading(true);
      setError(null);

      const currentSubdomain = getSubdomain();
      setSubdomain(currentSubdomain);

      // Check if this is the admin portal
      if (isAdminSubdomain()) {
        setIsAdmin(true);
        setBranding(ADMIN_BRANDING);
        applyBrandingColors(ADMIN_BRANDING.colors);
        updateDocumentTitle('Admin Portal');
        setIsLoading(false);
        return;
      }

      // No subdomain - use default branding
      if (!currentSubdomain) {
        setBranding(DEFAULT_BRANDING);
        applyBrandingColors(DEFAULT_BRANDING.colors);
        updateDocumentTitle('Quote This Job');
        setIsLoading(false);
        return;
      }

      // Fetch tenant branding from API
      try {
        const tenantData = await fetchTenantBranding(currentSubdomain);

        setTenant(tenantData);
        const tenantBranding = {
          companyDisplayName: tenantData.branding?.companyDisplayName || tenantData.companyName,
          logoUrl: tenantData.branding?.logoUrl,
          faviconUrl: tenantData.branding?.faviconUrl,
          faviconUrls: tenantData.branding?.faviconUrls,
          touchIconUrl: tenantData.branding?.touchIconUrl,
          msTileImageUrl: tenantData.branding?.msTileImageUrl,
          loginBackgroundUrl: tenantData.branding?.loginBackgroundUrl,
          colors: {
            ...DEFAULT_BRANDING.colors,
            ...tenantData.branding?.colors,
          },
        };

        setBranding(tenantBranding);
        applyBrandingColors(tenantBranding.colors);
        updateFavicons(tenantBranding);
        updateDocumentTitle(tenantBranding.companyDisplayName);
      } catch (err) {
        setError(err.message);
        // Still apply default branding on error
        setBranding(DEFAULT_BRANDING);
        applyBrandingColors(DEFAULT_BRANDING.colors);
      } finally {
        setIsLoading(false);
      }
    }

    initialize();
  }, [fetchTenantBranding]);

  const value = {
    tenant,
    tenantId: tenant?.tenantId || null,
    branding,
    subdomain,
    isAdmin,
    isLoading,
    error,
    // Helper to check if we're on a tenant subdomain
    isTenantPortal: !!subdomain && !isAdmin,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant context
 */
export function useTenant() {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
}

/**
 * Hook to access just the branding
 */
export function useTenantBranding() {
  const { branding } = useTenant();
  return branding;
}

export default TenantContext;
